import FingerprintJS from '@fingerprintjs/fingerprintjs-pro';
import { cfgBrand, cfgFingerprintjs, cfgFingerprintjsHost, cfgFingerprintVersions, cfgLateFingerprint, getRealm, stateCountry, stateRealm } from '@infinitro/config';
import { Api, getStoredPublicKey, getUnvalidatedPlayerSession, initAnalytics, locale, loggedIn, ModalDialog, posthog, pushModal, refreshUuid, refreshValue, Stage, stage, stateShowGeoBlockFooter, stateShowSeal, stateShowSealText, stateVisit, storeUuid, storeValue } from '@infinitro/global';
import { get, writable } from 'svelte/store';
import { uuidv7 } from 'uuidv7';
import { PreAuth, PreAuthLast, 
// ServerTime,
Status, UpdateVisit, Visit } from './api/api';
export { stateRealm, stateVisit };
const MAX_RETRIES = 5;
let retries = 0;
const DEBUG = stage !== Stage.Prod;
function log(msg) {
    if (DEBUG)
        console.log(msg);
}
const isUUID = (v) => /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(v);
const uuidOrNull = (v) => (v && isUUID(v) ? v : undefined);
const VERSION = import.meta.env.VITE_APP_VERSION;
const FINGERPRINTJS = cfgFingerprintjs();
const FINGERPRINT_VERSIONS = cfgFingerprintVersions();
const FINGERPRINT_VERSION = FINGERPRINT_VERSIONS?.[0] ?? 'v3.9.7';
const LATE_FINGERPRINT = cfgLateFingerprint();
const fpHost = cfgFingerprintjsHost();
const brand = cfgBrand();
function getIsFramed() {
    return typeof window !== 'undefined' && window !== window.parent;
}
let fpAgent = undefined;
let realm = getRealm();
stateRealm.subscribe((value) => {
    if (realm === value)
        return;
    log(`visit realm change to ${value}`);
    realm = value;
    initialized = false;
    if (typeof window !== 'undefined') {
        init();
    }
});
let fingerprint = undefined;
export const stateFingerprint = writable(undefined);
// Attribution tracking stores - populated from URL params on each page load
export const stateStag = writable(null);
export const stateCampaignCode = writable(null);
export const stateCampaignShortcode = writable(null);
const matchShortCode = /^\?([A-Za-z2-7]{6})$/;
const matchShortCode2 = /^\?([A-Za-z2-7]{6})&/;
const matchActionSlug = /[\/\?\&]*([A-Za-z0-9]{1}\-[A-za-z0-9]{12})/;
const sleep = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));
const getSessionVisitId = () => {
    const stateVisitId = uuidOrNull(get(stateVisit));
    log(`stateVisitId: ${stateVisitId}`);
    const realmSessionVisitId = uuidOrNull(sessionStorage.getItem(`${realm}_sessionVisitId`));
    log(`realmSessionVisitId: ${realmSessionVisitId}`);
    const sessionVisitId = stateVisitId ?? realmSessionVisitId ?? null;
    return sessionVisitId;
};
const setSessionVisitId = (id) => {
    sessionStorage.setItem(`${realm}_sessionVisitId`, id);
    stateVisit.set(uuidOrNull(id));
};
let playerVersion = undefined;
const getPlayerVersion = async () => {
    try {
        if (playerVersion)
            return playerVersion;
        if (typeof window !== 'undefined' && window.location.hostname.includes('localhost')) {
            playerVersion = VERSION;
            return VERSION;
        }
        const res = await fetch(`/player/version.json?` + new Date().getTime().toString());
        const ver = await res.json();
        playerVersion = ver?.version ?? undefined;
        return playerVersion;
    }
    catch (e) {
        console.log(`error acquiring player site version: ${e}`);
    }
    return undefined;
};
let authBuild = undefined;
const getAuthBuild = async () => {
    try {
        if (authBuild !== undefined)
            return authBuild;
        const playerVersion = await getPlayerVersion();
        if (!playerVersion) {
            authBuild = 0;
        }
        else {
            authBuild = parseInt(playerVersion.split('.')[2]) ?? 0;
        }
        return authBuild;
    }
    catch {
        console.log(`error parsing auth version: ${playerVersion}`);
        posthog.capture(`error parsing auth version`, { data: playerVersion });
    }
    return 0;
};
let appBuild = undefined;
const getAppBuild = () => {
    try {
        if (appBuild !== undefined)
            return appBuild;
        appBuild = parseInt(VERSION.split('.')[2]) ?? 0;
        return appBuild;
    }
    catch {
        console.log(`error parsing auth version: ${VERSION}`);
        posthog.capture(`error parsing auth version`, { data: VERSION });
    }
    return 0;
};
const getLandingPage = () => {
    if (typeof window === 'undefined') {
        return new URL('http://localhost/');
    }
    if (getIsFramed()) {
        try {
            return new URL(parent.location.href);
        }
        catch (e) {
            log(`error acquiring framed href: ${e}`);
        }
    }
    return new URL(window.location.href);
};
const getShortCode = () => {
    const page = getLandingPage();
    const search = page.search;
    if (matchShortCode.test(search))
        return search.substring(1);
    if (matchShortCode2.test(search))
        return search.substring(1, 7);
    return page.searchParams.get('r');
};
export const getActionSlug = () => {
    const page = getLandingPage();
    const search = page.search.toLowerCase();
    const path = page.pathname.toLowerCase();
    if (matchActionSlug.test(path))
        return matchActionSlug.exec(path)?.[1] ?? null;
    if (matchActionSlug.test(search))
        return matchActionSlug.exec(search)?.[1] ?? null;
    return null;
};
const getReferrer = () => {
    if (typeof window === 'undefined')
        return null;
    if (getIsFramed()) {
        try {
            if (parent.document.referrer) {
                return new URL(parent.document.referrer);
            }
        }
        catch (e) {
            log(`error acquiring framed referer: ${e}`);
        }
    }
    return document.referrer ? new URL(document.referrer) : null;
};
const attribution = async (page) => {
    const stag = page.searchParams.get('stag');
    const campaignCode = page.searchParams.get('campaign');
    const campaignShortcode = getShortCode();
    return [
        stag ? stag : null,
        campaignCode ? campaignCode : null,
        campaignShortcode ? campaignShortcode : null
    ];
};
export const getEarlyFingerPrint = async () => {
    if (fingerprint) {
        stateFingerprint.set(fingerprint);
        return fingerprint;
    }
    fingerprint = await refreshValue('fingerprint');
    if (fingerprint)
        stateFingerprint.set(fingerprint);
    return fingerprint;
};
export const getLateFingerPrint = async () => {
    if (!FINGERPRINTJS || !fpHost)
        return undefined;
    if (fingerprint) {
        stateFingerprint.set(fingerprint);
        return fingerprint;
    }
    fingerprint = await getEarlyFingerPrint();
    if (fingerprint)
        return fingerprint;
    if (!fpAgent) {
        try {
            fpAgent = await FingerprintJS.load({
                apiKey: FINGERPRINTJS,
                endpoint: `https://${fpHost}`,
                scriptUrlPattern: `https://${fpHost}/web/v3/${FINGERPRINTJS}/loader_${FINGERPRINT_VERSION}.js`
            });
        }
        catch (e) {
            console.log(`error loading fingerprint library: ${e}`);
            posthog.capture(`error loading fingerprint library`, { error: e });
            return undefined;
        }
    }
    if (fpAgent) {
        try {
            const result = await fpAgent.get();
            log(`fingerprint result: ${JSON.stringify(result, null, 2)}`);
            if (result && result.visitorId) {
                fingerprint = result.visitorId;
                stateFingerprint.set(fingerprint);
                await storeValue('fingerprint', fingerprint);
                return fingerprint;
            }
        }
        catch (e) {
            console.log(`error acquiring fingerprint: ${e}`);
            posthog.capture(`error acquiring fingerprint`, { error: e, late: LATE_FINGERPRINT });
            return undefined;
        }
    }
    return fingerprint;
};
const formatGeoLabel = (name, code) => name && code ? `${name} (${code})` : (name ?? code ?? '');
const formatRegionGeoLabel = (regionName, countryName, regionCode, countryCode) => {
    const names = [regionName, countryName].filter(Boolean).join(', ');
    const codes = [regionCode, countryCode].filter(Boolean).join(', ');
    return codes ? `${names} (${codes})` : names;
};
const checkGeoblock = (data) => {
    if (!(!authBuild || authBuild > 0)) {
        log(`skipping geoblock check if auth build is not available (robots.txt restricted access)`);
        return;
    }
    if (data.countryCode) {
        console.log(`setting country code: ${data.countryCode}`);
        stateCountry.set(data.countryCode);
    }
    if (data.countryBanHard || data.countryBanSoft) {
        pushModal(ModalDialog.Geoblock, {
            label: formatGeoLabel(data.countryName, data.countryCode),
            hard: data.countryBanHard
        });
    }
    else if (data.regionBanHard || data.regionBanSoft) {
        pushModal(ModalDialog.Geoblock, {
            label: formatRegionGeoLabel(data.regionName, data.countryName, data.regionCode, data.countryCode),
            hard: data.countryBanHard
        });
    }
    if (data.countryBanHard ||
        data.countryBanSoft ||
        data.regionBanHard ||
        data.regionBanSoft ||
        data.regionBanLogin ||
        data.countryBanLogin ||
        data.regionBanReg ||
        data.countryBanReg ||
        data.regionBanSession ||
        data.countryBanSession) {
        stateShowGeoBlockFooter.set(true);
    }
    else {
        stateShowSealText.set(true);
        stateShowSeal.set(true);
    }
};
const updateVisit = async () => {
    log(`update visit...`);
    // sessionVisitId is set when the visit is logged to server
    const authBuild = await getAuthBuild();
    let sessionVisitId = getSessionVisitId();
    if (!sessionVisitId) {
        log(`retry visit...`);
        await retryInit(`updateVisit`);
        sessionVisitId = getSessionVisitId();
        if (!sessionVisitId) {
            console.log(`error acquiring visit`);
            posthog.capture(`error acquiring visit`);
        }
        return;
    }
    if (sessionVisitId && isUUID(sessionVisitId))
        stateVisit.set(sessionVisitId);
    let [playerId, sessionId, sessionKid, sessionIat, sessionRealm] = await getUnvalidatedPlayerSession(realm);
    if (sessionRealm && sessionRealm != realm) {
        playerId = undefined;
        sessionId = undefined;
        sessionKid = undefined;
        sessionIat = undefined;
        sessionRealm = undefined;
    }
    const publicKey = (await getStoredPublicKey()) ?? ''; // todo: determine if we may need the option to skip this
    if (!fingerprint)
        await getLateFingerPrint();
    const update = await UpdateVisit({
        variables: {
            visitId: getSessionVisitId() ?? uuidv7(),
            sessionId,
            sessionKid,
            sessionIat,
            playerId,
            brand,
            publicKey,
            fingerprint,
            siteLanguageId: get(locale),
            appVersion: getAppBuild(),
            authVersion: authBuild
            // actionSlug: getActionSlug()
        },
        context: { api: 'public' }
    });
    log(`updated visit: ${JSON.stringify(update)}`);
    if (!update?.data?.updateVisit2)
        return;
    if (update.data.updateVisit2.statusCode === 'NOT_FOUND') {
        sessionStorage.removeItem(`${realm}_sessionVisitId`);
        sessionStorage.removeItem('sessionVisitId');
        log(`retry visit on update not found...`);
        await init(true);
        sessionVisitId = getSessionVisitId();
        if (!sessionVisitId) {
            console.log(`error acquiring visit on update`);
            posthog.capture(`error acquiring visit on update`);
        }
        return;
    }
    const data = update.data.updateVisit2;
    checkGeoblock(data);
};
// const subscribeServerTime = () => {
// 	console.log(`begin subscribed to server time`);
// 	try {
// 		ServerTime({ context: { api: 'public' } }).subscribe({
// 			next: (res) => {
// 				console.log(`server time is: ${res.data?.serverTime}`);
// 			}
// 		});
// 	} catch (e) {
// 		console.log(`error subscribing to server time: ${e}`);
// 	}
// 	console.log(`complete subscribed to server time`);
// };
let initialized = false;
const getClickIds = () => {
    const page = getLandingPage();
    const clickId = page.searchParams.get('clickid');
    const clickIds = localStorage.getItem('clickIds')?.split(',') ?? [];
    if (clickId && !clickIds.includes(clickId)) {
        clickIds.push(clickId);
        localStorage.setItem('clickIds', clickIds.join(','));
    }
    return clickIds;
};
const init = async (retry) => {
    if (initialized && !retry)
        return;
    const authBuild = await getAuthBuild();
    log(`init authBuild: ${authBuild}`);
    // Only skip the initial automatic init when auth build is unavailable.
    // Retries must still run, otherwise visit/preauth can never recover.
    if (!retry && !(authBuild > 0)) {
        console.log(`skipping automatic visit initialization if auth build is not available (robots.txt restricted access)`);
        return;
    }
    if (typeof window === 'undefined') {
        return;
    }
    initialized = true;
    if (retry) {
        retries++;
        if (retries > MAX_RETRIES) {
            console.log(`error initializing visit max retries reached`);
            posthog.capture(`error initializing visit max retries reached`);
            return;
        }
    }
    else {
        log(`Visit attribution version ${VERSION}`);
    }
    // subscribeServerTime();
    try {
        // thisVisitId is set before visit is logged to server
        const thisVisitId = uuidOrNull(sessionStorage.getItem(`${realm}_thisVisitId`)) ?? uuidv7();
        // sessionVisitId is set when the visit is logged to server
        const sessionVisitId = getSessionVisitId();
        if (sessionVisitId) {
            // todo: determine if this is a new landing versus an update
            // update visit tracks only a limited set of fields versus log visit
            await updateVisit();
            return;
        }
        const firstVisitId = (await refreshUuid(`${realm}_firstVisitId`)) ?? (await refreshUuid('firstVisitId'));
        const lastVisitId = (await refreshUuid(`${realm}_lastVisitId`)) ?? (await refreshUuid('lastVisitId'));
        log(`firstVisitId: ${firstVisitId}, lastVisitId: ${lastVisitId}, thisVisitId: ${thisVisitId}`);
        let [playerId, sessionId, sessionKid, sessionIat, sessionRealm] = await getUnvalidatedPlayerSession(realm);
        if (sessionRealm !== realm) {
            playerId = undefined;
            sessionId = undefined;
            sessionKid = undefined;
            sessionIat = undefined;
            sessionRealm = undefined;
        }
        const publicKey = (await getStoredPublicKey()) ?? ''; // todo: determine if we may need the option to skip this
        if (!fingerprint) {
            if (!LATE_FINGERPRINT)
                await getLateFingerPrint();
            else
                await getEarlyFingerPrint();
        }
        const languageLocale = navigator.language;
        const userLanguageId = languageLocale.length >= 2 ? languageLocale.substring(0, 2) : 'en';
        const page = getLandingPage();
        const referer = getReferrer();
        const landingDomain = page ? page.hostname : null;
        const landingPage = page ? page.pathname : null;
        const landingQs = page ? page.search : null;
        const refererDomain = referer && referer.hostname != page.hostname ? referer.hostname : null;
        const refererPage = referer && referer.hostname != page.hostname ? referer.pathname : null;
        const refererQs = referer && referer.hostname != page.hostname ? referer.search : null;
        const utmSource = page.searchParams.get('utm_source');
        const utmMedium = page.searchParams.get('utm_medium');
        const utmCampaign = page.searchParams.get('utm_campaign') ?? page.searchParams.get('campaignid');
        const utmContent = page.searchParams.get('utm_content');
        const utmTerm = page.searchParams.get('utm_term');
        const [stag, campaignCode, campaignShortcode] = await attribution(page);
        const adCreativeId = page.searchParams.get('creativeid');
        const adCreativeType = page.searchParams.get('creativetype');
        const adPlacementId = page.searchParams.get('placementid');
        const adWalletId = page.searchParams.get('walletid');
        const adCountry = page.searchParams.get('country');
        const clickId = page.searchParams.get('clickid');
        const clickIds = getClickIds();
        console.log(`clickIds: ${clickIds}`);
        // Set attribution stores for reactive access in components
        stateStag.set(stag);
        stateCampaignCode.set(campaignCode);
        stateCampaignShortcode.set(campaignShortcode);
        const variables = {
            visitId: thisVisitId,
            firstVisitId,
            lastVisitId,
            sessionId,
            sessionKid,
            sessionIat,
            playerId,
            brand,
            publicKey,
            fingerprint,
            campaignCode,
            campaignShortcode,
            stag,
            landingDomain,
            landingPage,
            landingQs,
            refererDomain,
            refererPage,
            refererQs,
            utmSource,
            utmMedium,
            utmCampaign,
            utmContent,
            utmTerm,
            siteLanguageId: get(locale),
            userLanguageId,
            languageLocale,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
            timeZoneOffset: new Date().getTimezoneOffset() ?? 0,
            timeNow: Date.now(),
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            appVersion: getAppBuild(),
            authVersion: authBuild,
            actionSlug: getActionSlug(),
            clickId,
            adCreativeId,
            adCreativeType,
            adPlacementId,
            adWalletId,
            adCountry
        };
        log(JSON.stringify(variables, null, 2));
        sessionStorage.setItem('visitVariables', JSON.stringify(variables));
        const visit = await Visit({ variables, context: { api: 'public' } });
        if (!visit?.data?.logVisit2)
            return;
        const data = visit.data.logVisit2;
        if (data.visitId === thisVisitId) {
            if (data.realm)
                stateRealm.set(data.realm);
            setSessionVisitId(thisVisitId);
            if (!firstVisitId)
                storeUuid(`${realm}_firstVisitId`, thisVisitId);
            storeUuid(`${realm}_lastVisitId`, thisVisitId);
            // if (DEBUG) posthog.capture(`visit`, variables);
        }
        if (authBuild > 0) {
            checkGeoblock(data);
        }
        else {
            console.log(`authBuild is 0, skipping geoblock check`);
        }
        checkGeoblock(data);
    }
    catch (e) {
        initialized = false;
        console.log(`error logging visit: ${e}`);
        posthog.capture(`error logging visit`, { error: e });
    }
};
const retryInit = async (caller) => {
    if (caller)
        log(`retryInit from ${caller}`);
    let sessionVisitId = getSessionVisitId();
    if (sessionVisitId)
        return;
    await sleep(500);
    sessionVisitId = getSessionVisitId();
    if (sessionVisitId)
        return;
    await init(true);
};
export const preAuth = async () => {
    // sessionVisitId is set when the visit is logged to server
    const authBuild = await getAuthBuild();
    console.log(`authBuild: ${authBuild}`);
    let sessionVisitId = getSessionVisitId();
    if (!fingerprint)
        await getLateFingerPrint();
    if (!sessionVisitId) {
        await retryInit(`preAuth`);
        sessionVisitId = getSessionVisitId();
        if (!sessionVisitId) {
            console.log(`error acquiring visit`);
            posthog.capture(`error acquiring visit`);
            return undefined;
        }
    }
    stateVisit.set(sessionVisitId);
    const [playerId, sessionId, sessionKid, sessionIat] = await getUnvalidatedPlayerSession(realm);
    const publicKey = (await getStoredPublicKey()) ?? ''; // todo: determine if we may need the option to skip this
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let storedVariables = {};
    try {
        storedVariables = JSON.parse(sessionStorage.getItem('visitVariables') ?? '{}');
        log(JSON.stringify(storedVariables, null, 2));
    }
    catch (e) {
        console.log(`error parsing stored visit variables: ${e}`);
        posthog.capture(`error parsing stored visit variables`, { error: e });
    }
    log(`preauth: ${sessionVisitId}`);
    let preauth;
    try {
        preauth = await PreAuth({
            variables: {
                visitId: sessionVisitId,
                sessionId,
                sessionKid,
                sessionIat,
                playerId,
                brand,
                publicKey,
                fingerprint,
                siteLanguageId: get(locale),
                appVersion: getAppBuild(),
                authVersion: authBuild
            },
            context: { api: Api.public }
        });
    }
    catch {
        try {
            preauth = await PreAuthLast({
                variables: {
                    visitId: sessionVisitId,
                    sessionId,
                    sessionKid,
                    sessionIat,
                    playerId,
                    brand,
                    publicKey,
                    fingerprint,
                    siteLanguageId: get(locale),
                    appVersion: getAppBuild(),
                    authVersion: authBuild
                },
                context: { api: Api.public }
            });
        }
        catch (e) {
            log(`preauth failed: ${e}`);
        }
    }
    log(`preauth: ${JSON.stringify(preauth)}`);
    if (preauth?.data?.preauth?.statusCode === Status.MAINTENANCE) {
        pushModal(ModalDialog.FeatureUnavailable);
        return preauth.data?.preauth;
    }
    if (preauth?.data?.preauth?.statusCode === 'MISSING') {
        log(`retry visit on preauth visit not found...`);
        await init(true);
        sessionVisitId = getSessionVisitId();
        if (!sessionVisitId) {
            console.log(`error acquiring visit on update`);
            posthog.capture(`error acquiring visit on update`);
            return;
        }
        const preauth = await PreAuth({
            variables: {
                visitId: sessionVisitId,
                sessionId,
                sessionKid,
                sessionIat,
                playerId,
                brand,
                publicKey,
                fingerprint,
                siteLanguageId: get(locale),
                appVersion: getAppBuild(),
                authVersion: authBuild
            },
            context: { api: Api.public }
        });
        log(`preauth retry: ${JSON.stringify(preauth)}`);
        if (preauth.data?.preauth?.statusCode === Status.MAINTENANCE) {
            pushModal(ModalDialog.FeatureUnavailable);
        }
        return preauth?.data?.preauth;
    }
    return preauth?.data?.preauth;
};
// TODO: listen for network changes and update visit
const updateNetworkStatus = async () => {
    if (navigator.onLine) {
        log('You are online');
        await updateVisit();
    }
    else {
        log('You are offline');
        // Handle offline status
    }
};
let isLoggedIn = undefined;
loggedIn.subscribe((value) => {
    if (isLoggedIn === value)
        return;
    if (value === true) {
        isLoggedIn = true;
    }
    if (value === false && isLoggedIn === true) {
        isLoggedIn = false;
    }
    isLoggedIn = value;
});
if (typeof window !== 'undefined') {
    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);
    initAnalytics();
    setTimeout(async () => {
        try {
            log(`start initial visit initialization`);
            const authBuild = await getAuthBuild();
            if (!(authBuild > 0)) {
                log(`skipping automatic visit initialization if auth build is not available (robots.txt restricted access)`);
                return;
            }
            await init();
        }
        catch (e) {
            console.log(`error initializing visit: ${e}`);
            posthog.capture(`error initializing visit`, { error: e });
        }
    }, 100);
    // todo: add visit heartbeat
    try {
        if ('connection' in navigator) {
            console.log('Connection type:', navigator.connection.effectiveType);
            navigator.connection.addEventListener('change', async () => {
                console.log('Network changed:', navigator.connection.effectiveType);
                await updateVisit();
            });
        }
        else {
            console.log(`connection not found in navigator`);
        }
    }
    catch (e) {
        console.log(`error listening for network changes: ${e}`);
    }
}
